import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@brent/core-ui/ui'
import type { QuestionOption, DetailedVote, VoteResults } from '@/services/api/questions'

interface QuestionResultsProps {
  options: QuestionOption[]
  results: VoteResults
  votes: DetailedVote[]
}

export default function QuestionResults({ options, results, votes }: QuestionResultsProps) {
  const maxCount = Math.max(...Object.values(results.results), 1)

  // Build a map of option id -> text for display
  const optionMap = new Map(options.map((o) => [o.id, o.text]))

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Results</CardTitle>
          <CardDescription>
            {results.total_votes} {results.total_votes === 1 ? 'vote' : 'votes'} cast
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {options.map((option) => {
            const count = results.results[option.id] ?? 0
            const percentage =
              results.total_votes > 0 ? Math.round((count / results.total_votes) * 100) : 0
            const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0

            return (
              <div key={option.id} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{option.text}</span>
                  <span className="text-muted-foreground">
                    {count} ({percentage}%)
                  </span>
                </div>
                <div className="h-3 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>

      {votes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Individual Votes</CardTitle>
            <CardDescription>Detailed breakdown of how each member voted</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Vote</TableHead>
                  <TableHead>Voted At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {votes.map((vote) => (
                  <TableRow key={vote.user_id}>
                    <TableCell>{vote.user_email}</TableCell>
                    <TableCell>{optionMap.get(vote.option_id) ?? vote.option_id}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(vote.updated_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
